import type { CampaignAnalyticsKpi } from "@adpilot/shared";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  type XAxisTickContentProps,
  YAxis,
} from "recharts";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { useFormatters } from "@/lib/format";
import {
  dashKey,
  incompleteKey,
  type ShopComparisonRow,
  seriesKey,
  solidKey,
} from "./shop-comparison-data";

export interface ShopComparisonChartSeries {
  shopId: string;
  name: string;
  color: string;
  currency: string | null;
}

interface ShopComparisonChartProps {
  rows: ShopComparisonRow[];
  series: ShopComparisonChartSeries[];
  metricLabel: string;
  format: CampaignAnalyticsKpi["format"];
  /** Shared currency of every series, or null when they differ (axis drops the symbol). */
  axisCurrency: string | null;
}

function weekLabel(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
}

function useMetricFormatters(format: CampaignAnalyticsKpi["format"]) {
  const { locale, formatCurrency, formatNumber, formatPercent } = useFormatters();
  return {
    locale,
    value: (value: number, currency: string | null) => {
      if (format === "percent") return formatPercent(value, 1);
      if (format === "currency" && currency) return formatCurrency(value, currency);
      return formatNumber(value, { maximumFractionDigits: 2 });
    },
    axis: (value: number, currency: string | null) => {
      if (format === "percent") return formatPercent(value, 0);
      return formatNumber(value, {
        notation: "compact",
        maximumFractionDigits: 1,
        ...(format === "currency" && currency ? { style: "currency", currency } : {}),
      });
    },
  };
}

interface DotProps {
  cx?: number;
  cy?: number;
  payload?: ShopComparisonRow;
}

/** Weekly line per shop: solid between complete weeks, dashed into an incomplete one. */
export function ShopComparisonChart({
  rows,
  series,
  metricLabel,
  format,
  axisCurrency,
}: ShopComparisonChartProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const gradientPrefix = useId().replace(/:/g, "");
  const formatters = useMetricFormatters(format);
  const config = Object.fromEntries(
    series.map((item, index) => [seriesKey(index), { label: item.name, color: item.color }]),
  );

  return (
    <div
      role="img"
      aria-label={t("overview.shopComparison.chartAria", {
        metric: metricLabel,
        shops: series.map((item) => item.name).join(", "),
        weeks: rows.length,
      })}
    >
      <ChartContainer
        config={config}
        className="aspect-auto h-[236px] w-full min-w-0 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-ink"
        initialDimension={{ width: 698, height: 236 }}
      >
        <ComposedChart data={rows} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
          <defs>
            {series.map((item, index) => (
              <linearGradient
                key={item.shopId}
                id={`${gradientPrefix}-${index}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0" style={{ stopColor: item.color, stopOpacity: 0.14 }} />
                <stop offset="1" style={{ stopColor: item.color, stopOpacity: 0 }} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid vertical={false} stroke="var(--color-subtle)" />
          <XAxis
            dataKey="periodStart"
            scale="point"
            padding={{ left: 0, right: 0 }}
            height={26}
            axisLine={{ stroke: "var(--color-line)" }}
            tickLine={false}
            tickSize={0}
            tickMargin={0}
            minTickGap={8}
            tick={({ x, y, payload }: XAxisTickContentProps) => (
              <g transform={`translate(${x},${y})`}>
                <circle cy={2} r={2} fill="var(--color-line)" />
                <text
                  y={21}
                  textAnchor="middle"
                  className="fill-muted-foreground text-badge tabular-nums"
                >
                  {weekLabel(String(payload.value), formatters.locale)}
                </text>
              </g>
            )}
          />
          <YAxis
            // Fits "100%" or "$12.5k" at 11px plus the 8px tick margin.
            width={48}
            tickCount={5}
            axisLine={false}
            tickLine={false}
            tickMargin={8}
            domain={["auto", "auto"]}
            tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
            tickFormatter={(value: number) => formatters.axis(Number(value), axisCurrency)}
          />
          <ChartTooltip
            cursor={{ strokeDasharray: "3 3", strokeWidth: 1 }}
            wrapperStyle={{ outline: "none" }}
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as ShopComparisonRow | undefined;
              if (!active || !row) return null;
              const entries = series
                .map((item, index) => ({ ...item, value: row[seriesKey(index)] }))
                .filter((entry): entry is typeof entry & { value: number } =>
                  Number.isFinite(entry.value),
                )
                .sort((a, b) => b.value - a.value);
              return (
                <div className="flex w-[200px] flex-col gap-[5px] rounded-md bg-surface px-3 py-2.5 shadow-[var(--paper-flyout-shadow)]">
                  <div className="flex justify-between gap-2 text-badge text-muted-foreground">
                    <span className="font-medium tabular-nums">
                      {t("overview.shopComparison.weekOf", {
                        date: weekLabel(row.periodStart, formatters.locale),
                      })}
                    </span>
                    <span>{metricLabel}</span>
                  </div>
                  {entries.map((entry) => (
                    <div key={entry.shopId} className="flex items-center gap-2 text-xs">
                      <span
                        aria-hidden
                        className="size-2 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="min-w-0 flex-1 truncate text-text-secondary">
                        {entry.name}
                      </span>
                      <span className="font-medium text-ink tabular-nums">
                        {formatters.value(entry.value, entry.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }}
          />
          {series.map((item, index) => (
            <Area
              key={`area-${item.shopId}`}
              dataKey={seriesKey(index)}
              type="linear"
              stroke="none"
              fill={`url(#${gradientPrefix}-${index})`}
              connectNulls={false}
              activeDot={false}
              isAnimationActive={false}
              tooltipType="none"
            />
          ))}
          {series.flatMap((item, index) => [
            <Line
              key={`solid-${item.shopId}`}
              dataKey={solidKey(index)}
              type="linear"
              stroke={item.color}
              strokeWidth={1.5}
              strokeLinejoin="round"
              dot={false}
              activeDot={false}
              connectNulls={false}
              isAnimationActive={false}
              tooltipType="none"
            />,
            <Line
              key={`dash-${item.shopId}`}
              dataKey={dashKey(index)}
              type="linear"
              stroke={item.color}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              activeDot={false}
              connectNulls={false}
              isAnimationActive={false}
              tooltipType="none"
            />,
            <Line
              key={`markers-${item.shopId}`}
              dataKey={seriesKey(index)}
              type="linear"
              stroke="none"
              connectNulls={false}
              isAnimationActive={false}
              dot={({ cx, cy, payload }: DotProps) => {
                const key = `${item.shopId}-${payload?.periodStart}`;
                if (cx == null || cy == null || payload?.[seriesKey(index)] == null)
                  return <g key={key} />;
                const incomplete = payload[incompleteKey(index)] === true;
                return (
                  <circle
                    key={key}
                    cx={cx}
                    cy={cy}
                    r={3.5}
                    strokeWidth={1.5}
                    fill={incomplete ? "var(--color-surface)" : item.color}
                    stroke={incomplete ? item.color : "var(--color-surface)"}
                  />
                );
              }}
              activeDot={({ cx, cy }: DotProps) => (
                <circle
                  cx={cx}
                  cy={cy}
                  r={5}
                  strokeWidth={2}
                  fill={item.color}
                  stroke="var(--color-surface)"
                />
              )}
            />,
          ])}
        </ComposedChart>
      </ChartContainer>
    </div>
  );
}
